import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import {
  DEFAULT_ORDER_POLICY,
  type OrderPolicySettings,
  type OrderPolicySettingsPayload,
} from '../types/orderPolicy';

/**
 * 주문 기본설정 GET/PUT. be-order-lifecycle 계약 — order-service admin.
 * 게이트웨이 `/admin/v1/orders/**` → BE `/api/admin/v1/orders/**` RewritePath 재사용.
 * v2 envelope `{ success, data, error, meta }`. PUT 은 Idempotency-Key 수용(lenient).
 *
 * 경로가 generated OpenAPI 스펙에 미반영이라 `adminFetch`(raw 엔드포인트용 401 자동복구
 * fetch — single-flight refresh + 1회 retry)로 호출한다. 26필드 폼은 편집이 길어 편집 중
 * access token 만료 가능성이 높은데, adminFetch 가 overseasShipping/notifications 와 동일하게
 * 그 401 을 복구한다(adminHeaders + credentials 는 adminFetch 내부에서 부착).
 */
const PATH = '/admin/v1/orders/policy-settings';

interface SettingsEnvelope {
  success: boolean;
  data?: OrderPolicySettings | null;
}

/**
 * 응답을 정규화한다 — BE 가 일부 필드를 누락/널로 줄 수 있어 기본값으로 채워
 * 폼이 항상 controlled 상태를 유지하도록 한다(undefined → controlled 경고 방지).
 */
const normalize = (
  s: Partial<OrderPolicySettings> | null | undefined,
): OrderPolicySettings => ({
  // 기존 6
  paymentDueHours: s?.paymentDueHours ?? DEFAULT_ORDER_POLICY.paymentDueHours,
  unpaidAutoCancelEnabled:
    s?.unpaidAutoCancelEnabled ?? DEFAULT_ORDER_POLICY.unpaidAutoCancelEnabled,
  autoReadyOnPayment: s?.autoReadyOnPayment ?? DEFAULT_ORDER_POLICY.autoReadyOnPayment,
  autoConfirmEnabled: s?.autoConfirmEnabled ?? DEFAULT_ORDER_POLICY.autoConfirmEnabled,
  autoConfirmDays: s?.autoConfirmDays ?? DEFAULT_ORDER_POLICY.autoConfirmDays,
  soldoutOrderBlock: s?.soldoutOrderBlock ?? DEFAULT_ORDER_POLICY.soldoutOrderBlock,
  // 신규 정책 11
  unpaidAutoCancelDays: s?.unpaidAutoCancelDays ?? DEFAULT_ORDER_POLICY.unpaidAutoCancelDays,
  autoDeliveryCompleteEnabled:
    s?.autoDeliveryCompleteEnabled ?? DEFAULT_ORDER_POLICY.autoDeliveryCompleteEnabled,
  autoDeliveryCompleteDays:
    s?.autoDeliveryCompleteDays ?? DEFAULT_ORDER_POLICY.autoDeliveryCompleteDays,
  purchaseOfferReconfirm:
    s?.purchaseOfferReconfirm ?? DEFAULT_ORDER_POLICY.purchaseOfferReconfirm,
  refundProgressReconfirm:
    s?.refundProgressReconfirm ?? DEFAULT_ORDER_POLICY.refundProgressReconfirm,
  claimRequestEnabled: s?.claimRequestEnabled ?? DEFAULT_ORDER_POLICY.claimRequestEnabled,
  claimRequestOrderMark:
    s?.claimRequestOrderMark ?? DEFAULT_ORDER_POLICY.claimRequestOrderMark,
  claimRequestVendorApply:
    s?.claimRequestVendorApply ?? DEFAULT_ORDER_POLICY.claimRequestVendorApply,
  autoRefundEnabled: s?.autoRefundEnabled ?? DEFAULT_ORDER_POLICY.autoRefundEnabled,
  stockDecrementTiming:
    s?.stockDecrementTiming ?? DEFAULT_ORDER_POLICY.stockDecrementTiming,
  benefitGrantTiming: s?.benefitGrantTiming ?? DEFAULT_ORDER_POLICY.benefitGrantTiming,
  // 신규 클레임 복원 9
  cancelRestoreStock: s?.cancelRestoreStock ?? DEFAULT_ORDER_POLICY.cancelRestoreStock,
  cancelRestoreCoupon: s?.cancelRestoreCoupon ?? DEFAULT_ORDER_POLICY.cancelRestoreCoupon,
  cancelGrantFreebie: s?.cancelGrantFreebie ?? DEFAULT_ORDER_POLICY.cancelGrantFreebie,
  exchangeRestoreCoupon:
    s?.exchangeRestoreCoupon ?? DEFAULT_ORDER_POLICY.exchangeRestoreCoupon,
  exchangeGrantFreebie: s?.exchangeGrantFreebie ?? DEFAULT_ORDER_POLICY.exchangeGrantFreebie,
  exchangeGrantMileage: s?.exchangeGrantMileage ?? DEFAULT_ORDER_POLICY.exchangeGrantMileage,
  exchangeGrantCouponMileage:
    s?.exchangeGrantCouponMileage ?? DEFAULT_ORDER_POLICY.exchangeGrantCouponMileage,
  refundRestoreStock: s?.refundRestoreStock ?? DEFAULT_ORDER_POLICY.refundRestoreStock,
  refundRestoreCoupon: s?.refundRestoreCoupon ?? DEFAULT_ORDER_POLICY.refundRestoreCoupon,
  // 감사 메타
  updatedBy: s?.updatedBy ?? null,
  updatedAt: s?.updatedAt ?? null,
});

export const orderPolicyQueryKey = () => ['admin', 'order-policy-settings'] as const;

const fetchSettings = async (): Promise<OrderPolicySettings> => {
  const res = await adminFetch(PATH);
  if (!res.ok) throw new Error(`order policy settings fetch failed: ${res.status}`);
  const env = (await res.json()) as SettingsEnvelope;
  return normalize(env.data);
};

export const useOrderPolicyQuery = () =>
  useQuery<OrderPolicySettings>({
    queryKey: orderPolicyQueryKey(),
    queryFn: fetchSettings,
  });

export const useOrderPolicyMutation = () => {
  const qc = useQueryClient();
  return useMutation<OrderPolicySettings, Error, OrderPolicySettingsPayload>({
    mutationFn: async (payload) => {
      const res = await adminFetch(PATH, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', PATH, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`order policy settings update failed: ${res.status}`);
      const env = (await res.json()) as SettingsEnvelope;
      return normalize(env.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(orderPolicyQueryKey(), data);
      void qc.invalidateQueries({ queryKey: orderPolicyQueryKey() });
    },
  });
};
